import { UpperCasePipe } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { Icon } from '../icon/icon';
import { LANGUAGES } from '../../../core/i18n';
import { LanguageService } from '../../../core/i18n';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-language-selector',
  imports: [Icon, UpperCasePipe, TranslatePipe],
  template: `
    <button type="button" (click)="toggle()">
      <app-icon name="globe"></app-icon>
      {{ currentLang() ?? '' | uppercase }}
    </button>

    @if (open()) {
      <div class="lang-overlay">
        <div class="lang-panel">
          <button class="close-btn" (click)="close()">
            <app-icon name="close" fill="transparent" stroke="currentColor"></app-icon>
          </button>
          <ul class="lang-list">
            @for (lang of languages; track lang.code) {
              <li>
                <button type="button" (click)="select(lang.code)">
                  {{ lang.translationKey | translate }} {{ lang.abbrTranslationKey | translate }}
                </button>
              </li>
            }
          </ul>
        </div>
      </div>
    }
  `,
  styles: `
    :host {
      display: inline-flex;
    }

    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      line-height: 1;
      white-space: nowrap;
      color: var(--color-text);
      text-transform: uppercase;

      &:hover {
        color: var(--color-text-strong);
      }
    }

    .lang-overlay {
      position: fixed;
      inset: 0;
      background: rgba(255, 255, 255, 0.9);
      backdrop-filter: blur(0.125rem);
      z-index: 1000;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding-top: 4.5rem;

      .lang-panel {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding-top: 2rem;

        .close-btn {
          position: absolute;
          top: 0;
          right: -3rem;
          background: none;
          border: none;
          cursor: pointer;
          color: var(--color-text);
        }
      }

      .lang-list {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 2rem 5rem;
        text-align: center;

        button {
          color: var(--color-text);
          font-size: 0.875rem;
          text-transform: uppercase;

          &:hover {
            color: var(--color-text-strong);
          }
        }
      }
    }

    @media (max-width: 768px) {
      .lang-overlay {
        display: none;
      }
    }
  `,
})
export class LanguageSelector {
  private languageService = inject(LanguageService);

  open = signal<boolean>(false);

  protected readonly currentLang = this.languageService.currentLang;
  languages = LANGUAGES;

  close() {
    this.open.set(false);
  }

  toggle() {
    this.open.update((v) => !v);
  }

  select(lang: string) {
    this.languageService.switchLanguage(lang);
    this.close();
  }
}
